import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { blogPosts } from '@/mocks/blog';
import { useSEO } from '@/hooks/useSEO';

const WHATSAPP_URL =
  'https://wa.me/5581982030285?text=Ol%C3%A1%2C%20acabei%20de%20solicitar%20uma%20visita%20t%C3%A9cnica%20pelo%20site%20e%20gostaria%20de%20confirmar!';

function CountdownBadge() {
  const [seconds, setSeconds] = useState(59);
  useEffect(() => {
    if (seconds <= 0) return;
    const t = setTimeout(() => setSeconds(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds]);

  return (
    <span className="tabular-nums font-bold text-[#E8610A]">
      00:{String(seconds).padStart(2, '0')}
    </span>
  );
}

export default function ObrigadoPage() {
  const [searchParams] = useSearchParams();
  const fromSlug = searchParams.get('from');

  useSEO({
    title: 'Solicitação Recebida | Home Office Premium',
    description: 'Recebemos sua solicitação de visita técnica gratuita. Nossa equipe entrará em contato em breve.',
    path: '/obrigado',
    noindex: true,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const originPost = blogPosts.find(p => p.slug === fromSlug);
  const suggestedPosts = blogPosts
    .filter(p => p.slug !== fromSlug)
    .sort(() => 0.5 - Math.random())
    .slice(0, 3);

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero de confirmação */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          {/* Ícone animado */}
          <div className="w-20 h-20 flex items-center justify-center mx-auto rounded-full bg-[#E8610A]/10 mb-6"
            style={{ animation: 'pop 0.5s ease-out' }}>
            <i className="ri-checkbox-circle-fill text-[#E8610A] text-4xl"></i>
          </div>

          <span className="inline-block bg-[#FFF3EC] text-[#E8610A] text-xs font-semibold px-4 py-1.5 rounded-full mb-5 uppercase tracking-widest">
            Solicitação confirmada
          </span>

          <h1
            className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Perfeito! Recebemos sua solicitação de visita técnica
          </h1>

          <p className="text-gray-500 text-base md:text-lg leading-relaxed max-w-xl mx-auto mb-8">
            Nossa equipe entrará em contato em até <strong className="text-gray-700">1 dia útil</strong> para
            agendar a visita no seu espaço — sem compromisso, sem enrolação.
          </p>

          {/* O que acontece agora */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
            {[
              { icon: 'ri-phone-line', step: '1', label: 'Ligamos para você', desc: 'Em até 1 dia útil' },
              { icon: 'ri-map-pin-line', step: '2', label: 'Visitamos seu espaço', desc: 'Data e hora a combinar' },
              { icon: 'ri-file-list-3-line', step: '3', label: 'Entregamos o projeto', desc: 'Diagnóstico + orçamento' },
            ].map(item => (
              <div
                key={item.step}
                className="bg-[#F9F6F3] rounded-2xl p-5 flex flex-col items-center text-center"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#E8610A]/15 mb-3">
                  <i className={`${item.icon} text-[#E8610A] text-lg`}></i>
                </div>
                <p className="font-semibold text-gray-900 text-sm mb-0.5">{item.label}</p>
                <p className="text-gray-400 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* CTA WhatsApp com urgência */}
          <div className="bg-[#1A1A1A] rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center gap-5 text-left">
            <div className="flex-1">
              <p className="text-white font-bold text-base md:text-lg leading-snug mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                Quer confirmar mais rápido?
              </p>
              <p className="text-gray-400 text-sm leading-relaxed">
                Chame no WhatsApp agora e um especialista responde em menos de{' '}
                <CountdownBadge /> minutos durante o horário comercial.
              </p>
            </div>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2.5 bg-green-500 hover:bg-green-600 text-white font-semibold text-sm px-6 py-3.5 rounded-full transition-colors whitespace-nowrap cursor-pointer flex-shrink-0"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              Falar no WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Divisor */}
      <div className="max-w-3xl mx-auto px-6">
        <div className="h-px bg-gray-100"></div>
      </div>

      {/* Artigos sugeridos */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block text-[#E8610A] text-xs font-semibold uppercase tracking-widest mb-2">
              Enquanto aguarda
            </span>
            <h2
              className="text-2xl md:text-3xl font-bold text-gray-900"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Leituras que vão preparar sua empresa
            </h2>
            <p className="text-gray-400 text-sm mt-2">
              Conteúdos práticos para você já ir pensando no projeto
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {suggestedPosts.map(post => (
              <Link
                key={post.id}
                to={`/blog/${post.slug}`}
                className="group border border-gray-100 rounded-2xl overflow-hidden hover:border-[#E8610A]/30 hover:-translate-y-1 transition-all duration-300 cursor-pointer bg-white"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src={post.coverImage}
                    alt={post.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-5">
                  <span className="text-[#E8610A] text-xs font-semibold">{post.category}</span>
                  <h3
                    className="text-sm font-bold text-gray-900 mt-1.5 mb-2 group-hover:text-[#E8610A] transition-colors line-clamp-2 leading-snug"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {post.title}
                  </h3>
                  <p className="text-gray-400 text-xs line-clamp-2 leading-relaxed mb-3">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">{formatDate(post.date)}</span>
                    <span className="flex items-center gap-1 text-[#E8610A] text-xs font-medium">
                      Ler artigo
                      <i className="ri-arrow-right-line text-xs"></i>
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#E8610A] transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xs"></i>
              Ver todos os artigos do blog
            </Link>
          </div>
        </div>
      </section>

      {/* Garantia / Social proof */}
      <section className="pb-16 px-6">
        <div className="max-w-4xl mx-auto bg-[#F9F6F3] rounded-2xl p-8 md:p-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {[
              { icon: 'ri-shield-check-line', value: '+500', label: 'Projetos entregues em PE' },
              { icon: 'ri-star-line', value: '4.9★', label: 'Avaliação média dos clientes' },
              { icon: 'ri-map-pin-line', value: 'Agreste', label: 'Atendimento em todo o Agreste' },
            ].map(item => (
              <div key={item.label} className="flex flex-col items-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#E8610A]/10 mb-3">
                  <i className={`${item.icon} text-[#E8610A] text-xl`}></i>
                </div>
                <p className="text-xl font-bold text-gray-900 mb-0.5">{item.value}</p>
                <p className="text-gray-500 text-sm">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      <style>{`
        @keyframes pop {
          0%   { transform: scale(0.5); opacity: 0; }
          70%  { transform: scale(1.1); opacity: 1; }
          100% { transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
